"use client";

import { Button } from "@/components/ui/button";
import type { WaxUserAccount } from "@/lib/wax";
import { useWalletAuth } from "@/hooks/useWalletAuth";
import {
  WalletSuccessDisplay,
  WalletErrorDisplay,
  WalletLoadingDisplay,
} from "@/components/wallet/WalletSuccessDisplay";

interface ExistingWalletLinkerProps {
  onAuthSuccess: (account: WaxUserAccount) => void;
  onAuthError: (error: string) => void;
  onAuthLoading?: (isLoading: boolean) => void;
  isLoading?: boolean;
  buttonStyle?: React.CSSProperties;
  primaryColor?: string;
}

/**
 * Simplified wallet connection component for linking existing WAX accounts.
 *
 * Uses the shared useWalletAuth hook for authentication state management
 * and WalletSuccessDisplay for consistent success/error UI.
 *
 * On testnet, Cloud Wallet is automatically excluded since accounts only exist on mainnet.
 */
export default function ExistingWalletLinker({
  onAuthSuccess,
  onAuthError,
  onAuthLoading,
  isLoading = false,
  buttonStyle,
  primaryColor,
}: ExistingWalletLinkerProps) {
  // Compute button style from primaryColor if provided
  const computedButtonStyle = buttonStyle || (primaryColor
    ? { backgroundColor: primaryColor, borderColor: primaryColor, color: '#FFFFFF' }
    : undefined);
  const {
    authState,
    authenticatedAccount,
    errorMessage,
    isAuthenticating,
    isTestnet,
    authenticateWithAnyWallet,
    getAvailableWallets,
    retry,
  } = useWalletAuth({
    onSuccess: onAuthSuccess,
    onError: onAuthError,
    onLoadingChange: onAuthLoading,
  });

  const availableWallets = getAvailableWallets();

  // Success state
  if (authState === 'success' && authenticatedAccount) {
    return (
      <WalletSuccessDisplay
        account={authenticatedAccount}
        title="Account Connected!"
        variant="card"
        className="max-w-md mx-auto"
      />
    );
  }

  // Error state
  if (authState === 'error') {
    return (
      <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg border border-gray-200">
        <WalletErrorDisplay
          message={errorMessage}
          onRetry={retry}
          variant="compact"
        />
      </div>
    );
  }

  // Connecting state
  if (authState === 'connecting' || authState === 'authenticating') {
    return (
      <WalletLoadingDisplay
        title="Connecting Your WAX Account..."
        message="Please select your wallet and complete the authentication..."
        variant="card"
        className="max-w-md mx-auto"
      />
    );
  }

  // Idle state - show connect button
  const walletNames = availableWallets.map(w => w.name).join(", ");

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg border border-gray-200">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>

        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Connect Your WAX Account
        </h3>

        <p className="text-gray-600 text-sm leading-relaxed">
          {isTestnet
            ? "Connect your existing WAX testnet account using Anchor or Wombat wallet."
            : "Connect your existing WAX account using any supported wallet."}
        </p>

        {isTestnet && (
          <p className="text-amber-600 text-xs mt-2">
            Note: WAX Cloud Wallet is not available on testnet.
          </p>
        )}
      </div>

      <div className="space-y-4">
        <Button
          onClick={authenticateWithAnyWallet}
          disabled={isLoading || isAuthenticating}
          className={computedButtonStyle ? "w-full text-white" : "w-full bg-blue-600 hover:bg-blue-700 text-white"}
          style={computedButtonStyle}
        >
          Connect Existing WAX Account
        </Button>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Supports {walletNames}
          </p>
        </div>
      </div>
    </div>
  );
}
